import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import {
  SHIFT_SHORT,
  TARGET_MINUTES,
  TIMEZONE,
  fmtTime,
  localYmd,
  windowFor,
  type Shift,
  type ShiftTimes,
} from '@siva/shared';
import { Avatar, Table } from '../lib/components.js';
import { fmtDate } from '../lib/ui.js';
import { t } from '../i18n/index.js';
import { usePhotos } from './photos.js';

/**
 * The Attendance day as one table with a side panel — layout A2, chosen by the owner on
 * 15 October 2026 from mock-ups, to replace four cards of names and five count tiles.
 *
 * The tabs are the counts and filter the table. On today, somebody whose shift has not started
 * yet is "Later Shift" rather than "Not Punched": at noon the 2nd shift was being listed with the
 * people who were actually late, and only the late ones should be red. That uses the shift times
 * in Settings, which are a timetable, not a rule — nothing else on the page or in the hours reads
 * them.
 */

type Base = {
  id: string;
  name: string;
  employment_type: 'full_time' | 'part_time' | null;
  shift: Shift;
  target_minutes: number;
  off_day: boolean;
  off_reason: 'weekly_off' | 'holiday' | null;
};
export type DayData = {
  work_date: string;
  is_today: boolean;
  server_time: string;
  holiday: string | null;
  punched_in: (Base & {
    in_time: string;
    running_minutes: number;
    weak_gps: boolean;
    location_unverified: boolean;
  })[];
  punched_out: (Base & {
    first_in: string;
    last_out: string;
    worked_minutes: number;
    below_target: boolean;
    auto_closed: boolean;
    weak_gps: boolean;
    location_unverified: boolean;
  })[];
  not_punched: Base[];
  off: Base[];
};
export type ComingUp = {
  today: string;
  shift_times: ShiftTimes;
  off_tomorrow: { date: string; published: boolean; workers: { id: string; name: string }[] };
  next_holiday: { date: string; name: string } | null;
  no_punchout_yesterday: number;
};

type Kind = 'working' | 'out' | 'not' | 'later' | 'off' | 'holiday';
type Row = Base & {
  kind: Kind;
  in: string | null;
  out: string | null;
  worked: number | null;
  /** "HH:MM" the shift starts, for a Later Shift row. */
  starts: string | null;
  badges: { text: string; tone: 'warn' | 'bad' }[];
};
const ORDER: Kind[] = ['working', 'out', 'not', 'later', 'off', 'holiday'];

const hm = (min: number) => `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, '0')}m`;
const toMin = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));
/** Minutes since midnight in the office's timezone. */
const officeMinutes = (iso: string) => {
  const s = new Intl.DateTimeFormat('en-GB', {
    timeZone: TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(iso));
  return toMin(s);
};
/** "14:00" → "2:00 PM". */
const clock = (hhmm: string) => {
  const m = toMin(hhmm);
  const h = Math.floor(m / 60);
  return `${h % 12 || 12}:${String(m % 60).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};

export function DayBoard({
  data,
  comingUp,
  earlyOuts,
  flagsOpen,
  previousDate,
}: {
  data: DayData;
  comingUp: ComingUp | null;
  earlyOuts: number;
  flagsOpen: number | null;
  previousDate: string | null;
}) {
  const { photoOf } = usePhotos();
  const [tab, setTab] = useState<'all' | Kind>('all');
  const [query, setQuery] = useState('');
  const isToday = data.is_today;

  // Now, in minutes, on the working day's own clock: between midnight and the cutoff the working
  // day is still yesterday's, so the hour after midnight is 24:xx, not 00:xx — otherwise every
  // 2nd-shift worker would read "Later Shift" again at half past twelve.
  const now = useMemo(() => {
    const m = officeMinutes(data.server_time);
    return localYmd(new Date(data.server_time), TIMEZONE) > data.work_date ? m + 1440 : m;
  }, [data.server_time, data.work_date]);

  const rows: Row[] = useMemo(() => {
    const flagBadges = (w: { weak_gps: boolean; location_unverified: boolean }) => [
      ...(w.weak_gps ? [{ text: t('day_weak_gps'), tone: 'warn' as const }] : []),
      ...(w.location_unverified ? [{ text: t('day_unverified'), tone: 'warn' as const }] : []),
    ];
    const out: Row[] = [
      ...data.punched_in.map((w) => ({
        ...w,
        kind: 'working' as const,
        in: w.in_time,
        out: null,
        worked: w.running_minutes,
        starts: null,
        badges: flagBadges(w),
      })),
      ...data.punched_out.map((w) => ({
        ...w,
        kind: 'out' as const,
        in: w.first_in,
        out: w.last_out,
        worked: w.worked_minutes,
        starts: null,
        badges: [
          ...(w.below_target ? [{ text: t('today_below_target'), tone: 'bad' as const }] : []),
          ...(w.auto_closed ? [{ text: t('punch_auto_closed'), tone: 'warn' as const }] : []),
          ...flagBadges(w),
        ],
      })),
      ...data.not_punched.map((w) => {
        const start = comingUp
          ? windowFor(comingUp.shift_times, w.employment_type, w.shift).start
          : null;
        const later = isToday && start !== null && toMin(start) > now;
        return {
          ...w,
          kind: (later ? 'later' : 'not') as Kind,
          in: null,
          out: null,
          worked: null,
          starts: later ? start : null,
          badges: [],
        };
      }),
      ...data.off.map((w) => ({
        ...w,
        kind: (w.off_reason === 'holiday' ? 'holiday' : 'off') as Kind,
        in: null,
        out: null,
        worked: null,
        starts: null,
        badges: [],
      })),
    ];
    return out.sort(
      (a, b) => ORDER.indexOf(a.kind) - ORDER.indexOf(b.kind) || a.name.localeCompare(b.name),
    );
  }, [data, comingUp, isToday, now]);

  const count = (k: Kind) => rows.filter((r) => r.kind === k).length;
  const tabs: { key: 'all' | Kind; label: string }[] = [
    { key: 'all', label: t('day_tab_all') },
    ...(isToday
      ? [
          { key: 'working' as const, label: t('day_tab_working') },
          { key: 'out' as const, label: t('today_out') },
          { key: 'not' as const, label: t('day_tab_not') },
          { key: 'later' as const, label: t('day_tab_later') },
        ]
      : [
          { key: 'out' as const, label: t('att_present') },
          { key: 'not' as const, label: t('att_absent') },
        ]),
    { key: 'off', label: t('att_off') },
    { key: 'holiday', label: t('hours_st_holiday') },
  ];
  const q = query.trim().toLowerCase();
  const shown = rows.filter(
    (r) => (tab === 'all' || r.kind === tab) && (!q || r.name.toLowerCase().includes(q)),
  );

  const statusOf = (r: Row) => {
    switch (r.kind) {
      case 'working':
        return <span className="badge good">{t('day_status_working')}</span>;
      case 'out':
        return <span className="badge violet">{isToday ? t('today_out') : t('att_present')}</span>;
      case 'not':
        return <span className="badge bad">{isToday ? t('day_status_not') : t('att_absent')}</span>;
      case 'later':
        return (
          <span className="badge neutral">
            {t('day_status_later', { time: clock(r.starts ?? '00:00') })}
          </span>
        );
      case 'off':
        return <span className="badge neutral">{t('att_off')}</span>;
      case 'holiday':
        return <span className="badge blue">{t('hours_st_holiday')}</span>;
    }
  };

  // The side panel. "Due" is everybody expected by now: in, out, or not in when they should be.
  const due = count('working') + count('out') + count('not');
  const arrived = count('working') + count('out');
  const pct = due > 0 ? Math.round((arrived / due) * 100) : 0;
  const laterRows = rows
    .filter((r) => r.kind === 'later')
    .sort((a, b) => toMin(a.starts!) - toMin(b.starts!) || a.name.localeCompare(b.name));

  return (
    <div className="day-board">
      <section className="card day-table-card">
        <div className="day-table-head">
          {/* [test] .counts: the tab labels carry the numbers the tiles used to. */}
          <div className="day-tabs counts" role="tablist" aria-label={t('day_filter')}>
            {tabs.map((tb) => {
              const n = tb.key === 'all' ? rows.length : count(tb.key);
              return (
                <button
                  key={tb.key}
                  type="button"
                  role="tab"
                  aria-selected={tab === tb.key}
                  className={`day-tab${tab === tb.key ? ' active' : ''}`}
                  onClick={() => setTab(tb.key)}
                >
                  {tb.label} <span className="day-tab-n">{n}</span>
                </button>
              );
            })}
          </div>
          <span className="search table-search" role="search">
            <Search size={15} className="search-icon" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('day_filter_name')}
              aria-label={t('day_filter_name')}
            />
          </span>
        </div>
        <Table className="stacks att-table">
          <thead>
            <tr>
              <th scope="col">{t('pay_worker')}</th>
              <th scope="col">{t('worker_status')}</th>
              <th scope="col">{t('reports_in')}</th>
              <th scope="col">{t('reports_out')}</th>
              <th scope="col">{t('day_worked_target')}</th>
              <th scope="col" className="ta-right">
                {t('nav_flags')}
              </th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              // Somebody working on their off day is shown against their normal day — the owner,
              // 15 October: the weekly off may simply be moved later. Display only; the day's
              // record keeps no target until the plan changes, and then it is recomputed.
              const target =
                r.target_minutes ||
                (r.off_day && r.off_reason === 'weekly_off' && r.employment_type
                  ? TARGET_MINUTES[r.employment_type]
                  : 0);
              const pctRow =
                r.worked !== null && target > 0 ? Math.min(100, (r.worked / target) * 100) : 0;
              return (
                <tr key={r.id} data-kind={r.kind}>
                  <td className="cell-name">
                    <span className="person">
                      <Avatar name={r.name} size={30} photoUrl={photoOf(r.id)} />
                      <span>
                        <Link to={`/workers/${r.id}?date=${data.work_date}`}>{r.name}</Link>
                        <small className="muted person-sub">
                          {r.employment_type === 'full_time' ? 'FT' : 'PT'} · {SHIFT_SHORT[r.shift]}
                          {r.off_day && r.kind !== 'off' && r.kind !== 'holiday' && (
                            <b className="off-day-note"> · {t('day_on_off_day')}</b>
                          )}
                        </small>
                      </span>
                    </span>
                  </td>
                  <td data-label={t('worker_status')}>{statusOf(r)}</td>
                  <td data-label={t('reports_in')} className="nowrap">
                    {r.in ? fmtTime(r.in) : <span className="muted">—</span>}
                  </td>
                  <td data-label={t('reports_out')} className="nowrap">
                    {r.out ? fmtTime(r.out) : <span className="muted">—</span>}
                  </td>
                  <td data-label={t('day_worked_target')}>
                    {r.worked === null ? (
                      <span className="muted">—</span>
                    ) : target === 0 ? (
                      // A holiday worked: nothing to measure against.
                      <span className="worked-bar">
                        <strong>{hm(r.worked)}</strong>
                      </span>
                    ) : (
                      <span className="worked-bar">
                        <span className="worked-track" aria-hidden="true">
                          <span
                            className={`worked-fill${pctRow >= 100 ? ' done' : ''}`}
                            style={{ width: `${pctRow}%` }}
                          />
                        </span>
                        <strong>{hm(r.worked)}</strong>
                        <small className="muted">
                          {t('day_of_target', { target: hm(target) })}
                        </small>
                      </span>
                    )}
                  </td>
                  <td data-label={t('nav_flags')} className="ta-right">
                    {r.badges.map((b) => (
                      <span key={b.text} className={`badge ${b.tone}`}>
                        {b.text}
                      </span>
                    ))}
                  </td>
                </tr>
              );
            })}
            {shown.length === 0 && (
              <tr>
                <td colSpan={6} className="muted empty-cell">
                  {t('day_none_here')}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </section>

      <aside className="day-side">
        <section className="card day-now">
          <span
            className="day-ring"
            style={{ '--p': pct } as React.CSSProperties}
            role="img"
            aria-label={`${pct}%`}
          >
            <span>{pct}%</span>
          </span>
          <span className="day-now-text">
            <strong>{isToday ? t('day_right_now') : t('day_that_day')}</strong>
            <span>{t('day_in_of_due', { n: arrived, due })}</span>
            {isToday && laterRows.length > 0 && (
              <span>{t('day_start_later', { n: laterRows.length })}</span>
            )}
          </span>
        </section>

        <section className="card day-decide" id="needs-decision">
          <h3>{t('day_needs_decision')}</h3>
          {earlyOuts === 0 && !flagsOpen && !comingUp?.no_punchout_yesterday ? (
            <p className="muted">{t('day_nothing_waiting')}</p>
          ) : (
            <ul className="day-decide-list">
              {earlyOuts > 0 && (
                <li className="tone-amber">
                  <span>{t('day_early_outs', { n: earlyOuts })}</span>
                  <a href="#early-outs">{t('early_outs_review')} →</a>
                </li>
              )}
              {!!flagsOpen && (
                <li className="tone-red">
                  <span>{t('day_flags_open', { n: flagsOpen })}</span>
                  <Link to="/flags">{t('day_see_flags')} →</Link>
                </li>
              )}
              {isToday && !!comingUp?.no_punchout_yesterday && previousDate && (
                <li className="tone-violet">
                  <span>
                    {t('day_no_punchout_yesterday', { n: comingUp.no_punchout_yesterday })}
                  </span>
                  <Link to={`/?date=${previousDate}`}>{t('day_open')} →</Link>
                </li>
              )}
            </ul>
          )}
        </section>

        {isToday && comingUp && (
          <>
            {/* Two cards, not one: the owner found the two lists run together (15 October). */}
            <section className="card day-coming">
              <h3>{t('day_coming_up')}</h3>
              <h4>{t('day_later_today')}</h4>
              {laterRows.length === 0 ? (
                <p className="muted">{t('day_nobody_later')}</p>
              ) : (
                <ul className="day-people">
                  {laterRows.map((r) => (
                    <li key={r.id}>
                      <Avatar name={r.name} size={28} photoUrl={photoOf(r.id)} />
                      <span className="grow">{r.name}</span>
                      <span className="muted">{clock(r.starts!)}</span>
                    </li>
                  ))}
                </ul>
              )}
              {comingUp.next_holiday && (
                <p className="day-holiday">
                  {t('day_next_holiday', {
                    name: comingUp.next_holiday.name,
                    date: fmtDate(comingUp.next_holiday.date, false),
                  })}
                </p>
              )}
            </section>
            <section className="card day-coming day-off-tomorrow">
              <h3>{t('day_off_tomorrow', { date: fmtDate(comingUp.off_tomorrow.date, false) })}</h3>
              {!comingUp.off_tomorrow.published ? (
                <p className="muted">{t('day_off_not_published')}</p>
              ) : comingUp.off_tomorrow.workers.length === 0 ? (
                <p className="muted">{t('day_nobody_off')}</p>
              ) : (
                <ul className="day-people">
                  {comingUp.off_tomorrow.workers.map((w) => (
                    <li key={w.id}>
                      <Avatar name={w.name} size={28} photoUrl={photoOf(w.id)} />
                      <span className="grow">{w.name}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </aside>
    </div>
  );
}
